/**
 * offline_slot_controller.js — show an offline edit in its place on the page
 * until the server applies it (Stimulus).
 *
 * Connects on every element drawn by templates/components/offline_slot.html.
 * Each slot names a record ("job:12") and a form field ("amount"). Of the
 * device's pending offline edits (the powersync_write_requests rows
 * offline_intent_controller.js queues, watched in _intent_watch.js), each slot
 * finds the latest edit of its record whose payload carries its field:
 *
 *   pending  the row is still on the device: the pending value shows in the
 *            slot's `pending` element, formatted by the slot's kind, with
 *            "Waiting to sync" beside it.
 *   synced   the server applied it (connector: 'powersync:intent-result'):
 *            the value stays with "Synced" until the page on screen is a copy
 *            rendered after the edit, when Jinja's own value is shown as is.
 *   refused  the server refused it: the slot shows the server's value again
 *            with the refusal message beside it.
 *   derived  the latest edit carries a field the server computes this value
 *            from (the slot's `derives` list, e.g. a line-item change under a
 *            total): the server value stays, dimmed, with "updates after sync".
 *            Business rules live only in application/services, so the device
 *            never recomputes it.
 *
 * It reads only intent rows and the slot's data attributes. It never
 * recomputes a value from other fields, never reads synced rows, and never
 * creates elements or inserts markup: it sets text on the elements the macro
 * drew, toggles `hidden`, and sets data-state
 * (tests/test_powersync_presentation_ssot.py). The sync banner's count stays
 * the page-wide indicator.
 */

import { Controller } from "@hotwired/stimulus";
import { formatDate, formatMoney, pageDisplayFormat } from "./_display_format.js";
import {
    intents, outcomes, pendingIds, recordOf, STATUS_TEXT, subscribe, unsubscribe,
} from "./_intent_watch.js";

/** The saved-state stamp of a version; ISO UTC, so it orders as text. */
function stampOf(version) {
    return (version || "").split(":").slice(2).join(":");
}

/** The latest edit of ``record`` that carries any of ``names``, as [id, intent]. */
function latestEdit(record, names) {
    let latest = null;
    intents.forEach((intent, id) => {
        if (intent.record !== record || !names.some((name) => intent.fields.has(name))) return;
        if (!latest || intent.requestedAt >= latest[1].requestedAt) latest = [id, intent];
    });
    return latest;
}

/** True when the page on screen was rendered after ``intent`` was applied. */
function pageShowsApplied(intent, outcome) {
    const page = document.body.dataset.recordVersion || "";
    if (recordOf(page) !== intent.record) return false;
    if (outcome.recordVersion) return stampOf(page) >= stampOf(outcome.recordVersion);
    return stampOf(page) > stampOf(intent.base);
}

const IDLE = { kind: "" };

/**
 * What a slot for ``field`` of ``record`` should say now. ``derives`` are the
 * fields the server computes the slot's value from: when the latest edit
 * carries one and not a value for ``field`` itself, the value waits for the
 * server ("derived"). A submitted value for the slot's own field is what the
 * server keeps (a typed price stands over its line items), so it shows as
 * pending. A slot with no field is purely derived and never shows a value or
 * a refusal of its own.
 */
function slotState(record, field, derives) {
    const edit = latestEdit(record, field ? [field, ...derives] : derives);
    if (!edit) return IDLE;
    const [id, intent] = edit;
    const pending = pendingIds.has(id);
    const outcome = outcomes.get(id);
    if (!pending && !outcome) return IDLE;
    if (outcome && !outcome.applied) {
        return field ? { kind: "refused", message: outcome.message } : IDLE;
    }
    if (!pending && pageShowsApplied(intent, outcome)) return IDLE;
    const value = field ? intent.fields.get(field) : "";
    if (!value && derives.some((name) => intent.fields.has(name))) return { kind: "derived" };
    return { kind: pending ? "pending" : "synced", value };
}

/** The words of submitted markup, one block per line. */
function plainText(html) {
    const doc = new DOMParser().parseFromString(html || "", "text/html");
    return [...doc.body.childNodes]
        .map((node) => node.textContent.trim())
        .filter(Boolean)
        .join("\n");
}

export default class extends Controller {
    static targets = ["server", "pending", "status"];
    static values = {
        entity: String,
        id: String,
        field: String,
        kind: { type: String, default: "text" },
        places: Number,
        labels: Object,
        derives: Array,
    };

    connect() {
        this._onChange = () => this.refresh();
        subscribe(this._onChange);
        this.refresh();
    }

    disconnect() {
        unsubscribe(this._onChange);
    }

    refresh() {
        const record = `${this.entityValue}:${this.idValue}`;
        const state = slotState(record, this.fieldValue, this.derivesValue);
        const showPending = this.hasPendingTarget && (state.kind === "pending" || state.kind === "synced");
        if (this.hasPendingTarget) {
            this.pendingTarget.textContent = showPending ? this.displayValue(state.value) : "";
            this.pendingTarget.hidden = !showPending;
        }
        this.serverTarget.hidden = showPending;
        const status = state.kind === "refused"
            ? `Not saved: ${state.message}` : (STATUS_TEXT[state.kind] || "");
        this.statusTarget.textContent = status;
        this.statusTarget.hidden = !status;
        this.statusTarget.dataset.state = state.kind;
        if (state.kind) this.element.dataset.state = state.kind;
        else delete this.element.dataset.state;
    }

    /** A submitted value as the page would draw it, by the slot's kind. */
    displayValue(raw) {
        const fmt = pageDisplayFormat();
        switch (this.kindValue) {
        case "money":
            return fmt ? formatMoney(raw, fmt, this.hasPlacesValue ? this.placesValue : undefined) : raw;
        case "date":
            return fmt ? formatDate(raw, fmt) : raw;
        case "rich":
            return plainText(raw);
        case "choice":
            return this.labelsValue[raw] ?? raw;
        default:
            return raw;
        }
    }
}
